/**
 * Serialise native audio-session calls, and let the last intent win.
 *
 * THE BUG THIS EXISTS FOR
 *
 * Every native session call was fire-and-forget (`void audioRouting.…`). When an
 * effect re-ran, React fired its cleanup and then its setup, so the bridge
 * received `setSpeakerphoneOn(false)` immediately followed by
 * `setSpeakerphoneOn(true)` with nothing sequencing them. The Capacitor bridge
 * is asynchronous and Android posts the work to another thread, so if the
 * `false` landed last the session stayed torn down for the rest of the circle.
 *
 * On Android that teardown is not a speaker toggle: it calls
 * `setMode(MODE_NORMAL)` and drops the whole VoIP routing path. On iOS it sets
 * the category to `.ambient` and deactivates the session, which is silence
 * rather than quiet. Same race, worse on the platform where nobody could hear.
 *
 * WHY A QUEUE AND NOT JUST CAREFUL CALLERS
 *
 * Session ownership has been moved so the teardown/setup pair should no longer
 * happen at a round boundary at all. This is the belt to that braces: the
 * ordering guarantee now lives in one place that every caller goes through,
 * rather than depending on each of them staying correct. Three call sites had
 * the fire-and-forget shape when this was written, and the next one to be added
 * would have inherited it.
 *
 * COALESCING
 *
 * Only the newest pending intent per key survives. A burst of toggles must
 * settle on what the caller asked for LAST, not replay every step — replaying
 * would put the session through a teardown it has already been told to undo,
 * which is the very failure being fixed.
 */

type Task = () => Promise<unknown>;

interface Pending {
  run: Task;
  resolve: () => void;
}

const running = new Set<string>();
const pending = new Map<string, Pending>();

/**
 * Run `task` for `key`, never concurrently with another task for the same key.
 *
 * Resolves when this call's intent has been applied, or when it has been
 * superseded by a newer one — in both cases the caller's goal is satisfied, so
 * neither is an error.
 */
export function enqueueAudioSessionTask(key: string, task: Task): Promise<void> {
  return new Promise<void>((resolve) => {
    if (running.has(key)) {
      // Supersede whatever was queued but not yet started. Its caller is
      // released too: a stale intent that will never run is not a failure, and
      // leaving that promise unresolved would hang anyone awaiting it.
      const superseded = pending.get(key);
      pending.set(key, { run: task, resolve });
      superseded?.resolve();

      return;
    }

    running.add(key);
    void drain(key, { run: task, resolve });
  });
}

async function drain(key: string, first: Pending): Promise<void> {
  let current: Pending | undefined = first;

  while (current) {
    try {
      await current.run();
    } catch {
      // Swallowed on purpose, matching the service's own rule: a routing
      // failure must never break a circle. The service already logs.
    }
    current.resolve();

    current = pending.get(key);
    pending.delete(key);
  }

  running.delete(key);
}

/** Test seam. Never call from application code. */
export function __resetAudioSessionQueue(): void {
  running.clear();
  pending.clear();
}
